
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProgressBar from '@/components/musicians/match/ProgressBar';
import { 
  Users, 
  Heart,
  UserCheck,
  UserMinus,
  Mail,
  MessageCircle,
  Calendar,
  Star,
  Plus,
  ArrowRight,
  MessagesSquare,
  Gift,
  UserPlus 
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// Types for fan segments
interface FanSegment {
  id: string;
  name: string;
  count: number;
  engagement: number;
  growth: number;
  icon: JSX.Element;
  color: string;
}

// Types for engagement actions
interface EngagementAction {
  id: string;
  title: string;
  description: string;
  audience: string;
  type: 'message' | 'content' | 'promotion' | 'event';
  icon: JSX.Element;
  priority: 'high' | 'medium' | 'low';
}

// Sample fan segments data
const FAN_SEGMENTS: FanSegment[] = [
  {
    id: 'superfans',
    name: 'Superfans',
    count: 287,
    engagement: 92,
    growth: 12,
    icon: <Heart className="h-4 w-4" />,
    color: 'bg-rose-500'
  },
  {
    id: 'active',
    name: 'Active Listeners',
    count: 1456,
    engagement: 68,
    growth: 7,
    icon: <UserCheck className="h-4 w-4" />,
    color: 'bg-blue-500'
  },
  {
    id: 'casual',
    name: 'Casual Listeners',
    count: 5823,
    engagement: 42,
    growth: 14,
    icon: <Users className="h-4 w-4" />,
    color: 'bg-amber-500'
  },
  {
    id: 'inactive',
    name: 'Inactive Fans',
    count: 2341,
    engagement: 11,
    growth: -3,
    icon: <UserMinus className="h-4 w-4" />,
    color: 'bg-gray-500'
  }
];

// Sample engagement actions
const ENGAGEMENT_ACTIONS: EngagementAction[] = [
  {
    id: '1',
    title: 'Superfan Appreciation',
    description: 'Send personalized thank you messages to your top 20 most engaged fans from last month',
    audience: 'superfans',
    type: 'message',
    icon: <Heart className="h-4 w-4" />,
    priority: 'high'
  },
  {
    id: '2',
    title: 'Exclusive Content Drop',
    description: 'Share a behind-the-scenes video from your latest studio session with your superfans',
    audience: 'superfans',
    type: 'content',
    icon: <Gift className="h-4 w-4" />,
    priority: 'medium'
  },
  {
    id: '3',
    title: 'Re-engagement Campaign',
    description: 'Send an email to inactive fans with a special discount code for your merchandise',
    audience: 'inactive',
    type: 'promotion',
    icon: <UserPlus className="h-4 w-4" />,
    priority: 'high'
  },
  {
    id: '4',
    title: 'Social Media Poll',
    description: 'Create an Instagram poll asking active listeners to vote on your next cover song',
    audience: 'active',
    type: 'content',
    icon: <MessagesSquare className="h-4 w-4" />,
    priority: 'medium'
  },
  {
    id: '5',
    title: 'Early Access Invitation',
    description: 'Invite active listeners to an early access stream of your upcoming release',
    audience: 'active',
    type: 'event',
    icon: <Calendar className="h-4 w-4" />,
    priority: 'low'
  }
];

const FanEngagementManager: React.FC = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("fan-segments");
  const [segments, setSegments] = useState(FAN_SEGMENTS);
  const [actions, setActions] = useState(ENGAGEMENT_ACTIONS);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyzeFans = () => {
    setIsAnalyzing(true);
    
    // Simulate analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      
      toast({
        title: "Fan analysis complete",
        description: "Your fan segments have been updated with the latest data"
      });
      
      // Simulate updated data
      setSegments(segments.map(segment => ({
        ...segment,
        engagement: Math.min(100, segment.engagement + Math.floor(Math.random() * 5)),
        growth: segment.growth + (Math.random() > 0.5 ? 2 : -1)
      })));
    }, 2000);
  };
  
  const handleActionExecute = (actionId: string) => {
    // Update action status
    setActions(actions.filter(a => a.id !== actionId));
    
    toast({
      title: "Engagement action executed",
      description: "Your engagement action has been scheduled and will be sent automatically"
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-1">Fan Engagement Manager</h2>
          <p className="text-muted-foreground">Segment your fans and nurture relationships strategically</p>
        </div>
        <div>
          <Button 
            onClick={handleAnalyzeFans} 
            disabled={isAnalyzing}
            variant="outline"
          >
            {isAnalyzing ? "Analyzing..." : "Analyze Fans"}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-background/70">
            <CardHeader className="pb-3">
              <Tabs 
                defaultValue={activeTab} 
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full"
              >
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="fan-segments">
                    <Users className="h-4 w-4 mr-2" />
                    Fan Segments
                  </TabsTrigger>
                  <TabsTrigger value="engagement-actions">
                    <Heart className="h-4 w-4 mr-2" />
                    Engagement Actions
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            
            <CardContent>
              <TabsContent value="fan-segments" className="mt-0 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {segments.map((segment) => (
                    <Card key={segment.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <span className={`p-1.5 rounded-full ${segment.color}`}>
                            {segment.icon}
                          </span>
                          {segment.name}
                          <Badge className="ml-auto">{segment.count}</Badge>
                        </CardTitle>
                        <CardDescription>
                          {segment.growth > 0 ? (
                            <span className="text-green-500">+{segment.growth}% growth</span>
                          ) : (
                            <span className="text-red-500">{segment.growth}% decline</span>
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <ProgressBar 
                          label="Engagement Rate" 
                          value={segment.engagement} 
                          color={segment.color}
                        />
                        
                        <div className="flex justify-between items-center text-sm mt-3">
                          <Button variant="outline" size="sm">View Fans</Button>
                          <Button variant="ghost" size="sm">
                            Target
                            <ArrowRight className="h-3.5 w-3.5 ml-1" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Fan Growth Insights</CardTitle>
                    <CardDescription>How your fan segments are evolving</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-md">
                        <h4 className="font-medium text-green-500 mb-1">Positive Trend</h4>
                        <p className="text-sm text-muted-foreground">Your superfan segment has grown by 12% in the last 30 days, likely due to your consistent engagement.</p>
                      </div>
                      
                      <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
                        <h4 className="font-medium text-amber-500 mb-1">Opportunity</h4>
                        <p className="text-sm text-muted-foreground">5,823 casual listeners could be converted to active fans with more consistent content.</p>
                      </div>
                      
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
                        <h4 className="font-medium text-blue-500 mb-1">Recommendation</h4>
                        <p className="text-sm text-muted-foreground">Focus on re-engaging your 2,341 inactive fans with a special offer or exclusive content.</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="engagement-actions" className="mt-0 space-y-4">
                <div className="space-y-4">
                  {actions.map((action) => (
                    <Card key={action.id}>
                      <div className="flex flex-col md:flex-row">
                        <div className="flex-1 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge 
                              variant="outline" 
                              className={`
                                flex items-center gap-1 
                                ${action.audience === 'superfans' ? 'bg-rose-500/10 text-rose-500 border-rose-500/30' : 
                                  action.audience === 'active' ? 'bg-blue-500/10 text-blue-500 border-blue-500/30' : 
                                  action.audience === 'casual' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' : 
                                  'bg-gray-500/10 text-gray-500 border-gray-500/30'}
                              `}
                            >
                              {action.audience === 'superfans' ? <Heart className="h-3.5 w-3.5" /> :
                               action.audience === 'active' ? <UserCheck className="h-3.5 w-3.5" /> :
                               action.audience === 'inactive' ? <UserMinus className="h-3.5 w-3.5" /> :
                               <Users className="h-3.5 w-3.5" />}
                              <span className="capitalize">{action.audience}</span>
                            </Badge>
                            
                            <Badge 
                              variant="outline" 
                              className={`
                                flex items-center gap-1 
                                ${action.priority === 'high' ? 'bg-red-500/10 text-red-500 border-red-500/30' : 
                                  action.priority === 'medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/30' : 
                                  'bg-green-500/10 text-green-500 border-green-500/30'}
                              `}
                            >
                              <Star className="h-3.5 w-3.5" />
                              <span className="capitalize">{action.priority} priority</span>
                            </Badge>
                          </div>
                          
                          <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                            {action.title}
                            <span className="p-1.5 rounded-full bg-muted/30">
                              {action.icon}
                            </span>
                          </h3>
                          <p className="text-muted-foreground">{action.description}</p>
                        </div>
                        
                        <div className="bg-muted/30 p-4 flex flex-col justify-center items-center md:w-48">
                          <Button 
                            onClick={() => handleActionExecute(action.id)} 
                            variant="default"
                            className="w-full mb-2"
                          >
                            Execute Action
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="w-full"
                          >
                            Customize
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  <div className="flex justify-center">
                    <Button variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Custom Action
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Fan Insights</CardTitle>
              <CardDescription>Key metrics about your fanbase</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-sm font-medium">Total Fan Base</h3>
                  <span className="font-medium">9,907</span>
                </div>
                <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500 rounded-full w-full"></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-sm font-medium">Active Engagement</h3>
                  <span className="font-medium">24.2%</span>
                </div>
                <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '24.2%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-sm font-medium">Fan Growth Rate</h3>
                  <span className="font-medium text-green-500">+8.7%</span>
                </div>
                <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '58.7%' }}></div>
                </div>
              </div>
              
              <div className="p-3 bg-muted/30 rounded-md mt-2">
                <h4 className="font-medium mb-1">Top Engagement Sources</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Instagram</span>
                    <span>42%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Spotify</span>
                    <span>27%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">TikTok</span>
                    <span>18%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Website</span>
                    <span>13%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Fan Engagement Tools</CardTitle>
              <CardDescription>Ways to connect with your audience</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start text-left">
                <Mail className="h-4 w-4 mr-2" />
                Create Email Campaign
              </Button>
              
              <Button variant="outline" className="w-full justify-start text-left">
                <MessageCircle className="h-4 w-4 mr-2" />
                Direct Messaging
              </Button>
              
              <Button variant="outline" className="w-full justify-start text-left">
                <Users className="h-4 w-4 mr-2" />
                Fan Community Portal
              </Button>
              
              <Button variant="outline" className="w-full justify-start text-left">
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Fan Events
              </Button>
              
              <Button variant="outline" className="w-full justify-start text-left">
                <Gift className="h-4 w-4 mr-2" />
                Exclusive Content Manager
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Integration Status</CardTitle>
              <CardDescription>Connected platforms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm">Spotify</span>
                </div>
                <Badge variant="outline" className="text-xs">Connected</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm">Instagram</span>
                </div>
                <Badge variant="outline" className="text-xs">Connected</Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm">YouTube</span>
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-6">Connect</Button>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm">TikTok</span>
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-6">Connect</Button>
              </div>
              
              <Separator className="my-2" />
              
              <Button variant="link" className="text-sm w-full justify-center">
                Manage Integrations
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FanEngagementManager;
